import { useEffect, useState, useCallback } from "react";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import useCompositionGuard from "../../hooks/useCompositionGuard";
import { CALL_STATUS_OPTIONS, CALL_STATUS_OTHERS, isStandardCallStatus } from "../../utils/callStatusOptions";
import { formatDate } from "../../utils/helpers";
import SearchableTLSelect from "../common/SearchableTLSelect";
import TeamPersonnelGroup from "../common/TeamPersonnelGroup";
import NapFieldsGroup, { napFieldsComplete, vacantPortsPayload } from "../common/NapFieldsGroup";
import NapCompletionCard from "../common/NapCompletionCard";
import ButtonSpinner from "../common/ButtonSpinner";
import FetchingOverlay from "../common/FetchingOverlay";
import NapCodeMapLinks from "../common/NapCodeMapLinks";
import { useConfirm } from "../../context/ConfirmContext";
import RescheduleModal from "./RescheduleModal";

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden mt-4 col-span-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 text-left text-sm font-medium text-gray-700"
      >
        {title}
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {children}
        </div>
      )}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
      <p className="text-sm text-gray-800 bg-white rounded px-3 py-1.5 border">{value || "—"}</p>
    </div>
  );
}

function ConfirmWithRemarkDialog({ title, message, confirmLabel, confirmClass, remark, onRemarkChange, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <label className="block text-sm font-medium mb-1">
          JO Status Remark <span className="text-red-500">*</span>
        </label>
        <textarea
          value={remark}
          onChange={(e) => onRemarkChange(e.target.value)}
          rows={3}
          placeholder="Enter JO Status Remark..."
          className="w-full border rounded px-3 py-2 text-sm mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!remark.trim()}
            onClick={onConfirm}
            className={`px-4 py-2 text-sm text-white rounded disabled:opacity-50 ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const CANCELLATION_REASON_OPTIONS = [
  "Cancelled by Subs",
  "Needs RTA",
  "Full NAP",
  "No Converge Facility",
  "Other reason",
];

function CancelJobOrderDialog({ teamNumber, onTeamNumberChange, personnel1Id, onPersonnel1IdChange, personnel2Id, onPersonnel2IdChange, remark, onRemarkChange, reasons, onReasonsChange, companyId, assignmentDate, teamLocked, onConfirm, onCancel }) {
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (teamNumber) {
      api.get("/team-masters/active", { params: { company_id: companyId } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === teamNumber);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const toggleReason = (reason) => {
    onReasonsChange(
      reasons.includes(reason)
        ? reasons.filter((r) => r !== reason)
        : [...reasons, reason]
    );
  };

  // Build combined remark: "Reason1; Reason2 - user typed text"
  const buildFinalRemark = () => {
    const prefix = reasons.length > 0 ? reasons.join("; ") : "";
    const text = remark.trim();
    if (prefix && text) return `${prefix} - ${text}`;
    if (text) return text;
    return "";
  };

  const canConfirm = remark.trim() && reasons.length > 0;

  const handleConfirmCancel = async () => {
    if (!canConfirm) return;
    const ok = await checkComp({ teamNumber, assignmentDate, personnel1Id, personnel2Id });
    if (!ok) return;
    onConfirm(buildFinalRemark(), reasons, teamNumber, personnel1Id, personnel2Id);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold mb-2">Cancel Job Order</h3>
        <p className="text-sm text-gray-600 mb-4">Are you sure you want to cancel this job order?</p>
        {teamLocked ? (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
            <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{teamNumber || "—"} <span title="Locked — assigned by another company">🔒</span></p>
          </div>
        ) : (
        <TeamPersonnelGroup
          teamMasterId={selectedTeamId}
          personnel1Id={personnel1Id}
          personnel2Id={personnel2Id}
          onTeamChange={(teamId, teamData) => {
            setSelectedTeamId(teamId);
            if (teamData) onTeamNumberChange(teamData.team_number);
            else onTeamNumberChange("");
          }}
          onPersonnel1Change={(id) => onPersonnel1IdChange(id)}
          onPersonnel2Change={(id) => onPersonnel2IdChange(id)}
          companyId={companyId}
          assignmentDate={assignmentDate}
        />
        )}
        <label className="block text-sm font-medium mb-1">
          JO Status Remarks <span className="text-red-500">*</span>
        </label>
        <textarea
          value={remark}
          onChange={(e) => onRemarkChange(e.target.value)}
          rows={3}
          placeholder="Enter JO Status Remarks..."
          className="w-full border rounded px-3 py-2 text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="block text-sm font-medium mb-1">
          Cancellation Reasons <span className="text-red-500">*</span>
        </label>
        <div className="space-y-1.5 mb-1">
          {CANCELLATION_REASON_OPTIONS.map((reason) => (
            <label key={reason} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={reasons.includes(reason)}
                onChange={() => toggleReason(reason)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {reason}
            </label>
          ))}
        </div>
        {reasons.length > 0 && remark.trim() && (
          <p className="text-xs text-gray-500 mt-2">
            Will save as: <span className="font-medium">{buildFinalRemark()}</span>
          </p>
        )}
        {!canConfirm && (
          <p className="text-xs text-red-500 mt-2">
            {reasons.length === 0 && !remark.trim()
              ? "Please select at least one cancellation reason and enter a remark."
              : reasons.length === 0
              ? "Please select at least one cancellation reason."
              : "Please enter a JO Status Remark."}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={handleConfirmCancel}
            className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-red-600 hover:bg-red-700"
          >
            Confirm
          </button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

function CompleteJobOrderDialog({ teamNumber, onTeamNumberChange, personnel1Id, onPersonnel1IdChange, personnel2Id, onPersonnel2IdChange, companyId, assignmentDate, teamLocked, remark, onRemarkChange, serialNumber, onSerialNumberChange, recordId, onRecordIdChange, napCode, onNapCodeChange, napId, onNapIdChange, napPort, onNapPortChange, vacantPorts, onVacantPortsChange, napPhoto, onNapPhotoChange, onConfirm, onCancel, submitting = false }) {
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();

  // Match team on mount
  useState(() => {
    if (teamNumber) {
      api.get("/team-masters/active", { params: { company_id: companyId } })
        .then(({ data }) => {
          const match = data.find((t) => t.team_number === teamNumber);
          if (match) setSelectedTeamId(match.id);
        }).catch(() => {});
    }
  });

  const handleConfirmComplete = async () => {
    const ok = await checkComp({ teamNumber, assignmentDate, personnel1Id, personnel2Id });
    if (!ok) return;
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <FetchingOverlay show={submitting} />
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b shrink-0">
          <h3 className="text-lg font-semibold mb-1">Complete Job Order</h3>
          <p className="text-sm text-gray-600">Are you sure you want to mark this job order as Completed?</p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {teamLocked ? (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{teamNumber || "—"} <span title="Locked — assigned by another company">🔒</span></p>
              </div>
            ) : (
            <TeamPersonnelGroup
              teamMasterId={selectedTeamId}
              personnel1Id={personnel1Id}
              personnel2Id={personnel2Id}
              onTeamChange={(teamId, teamData) => {
                setSelectedTeamId(teamId);
                if (teamData) onTeamNumberChange(teamData.team_number);
                else onTeamNumberChange("");
              }}
              onPersonnel1Change={(id) => onPersonnel1IdChange(id)}
              onPersonnel2Change={(id) => onPersonnel2IdChange(id)}
              companyId={companyId}
              assignmentDate={assignmentDate}
            />
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">
                JO Status Remarks <span className="text-red-500">*</span>
              </label>
              <textarea
                value={remark}
                onChange={(e) => onRemarkChange(e.target.value)}
                rows={3}
                placeholder="Enter JO Status Remarks..."
                className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <NapFieldsGroup
              napCode={napCode} onNapCodeChange={onNapCodeChange}
              napId={napId} onNapIdChange={onNapIdChange}
              napPort={napPort} onNapPortChange={onNapPortChange}
              vacantPorts={vacantPorts} onVacantPortsChange={onVacantPortsChange}
              showPhoto napPhoto={napPhoto} onNapPhotoChange={onNapPhotoChange}
              labelClassName="block text-xs font-medium text-gray-500 mb-0.5"
            />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">
                Modem Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => onSerialNumberChange(e.target.value)}
                placeholder="Enter Modem Serial Number..."
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">
                IPTV Serial Number
              </label>
              <input
                type="text"
                value={recordId}
                onChange={(e) => onRecordIdChange(e.target.value)}
                placeholder="Enter IPTV Serial Number..."
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 flex justify-end gap-2 px-6 py-4 border-t bg-white shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || !remark.trim() || !serialNumber.trim() || !teamNumber.trim() || !personnel1Id || !napFieldsComplete({ napCode, napId, napPort, vacantPorts }) || !napPhoto}
            onClick={handleConfirmComplete}
            className="px-4 py-2 text-sm text-white rounded disabled:opacity-50 bg-green-600 hover:bg-green-700 inline-flex items-center gap-2"
          >
            {submitting && <ButtonSpinner />}
            {submitting ? "Completing…" : "Confirm"}
          </button>
        </div>
      </div>
      <ConflictDialog />
    </div>
  );
}

const READ_ONLY = ["jo_number", "job_date", "account_number", "reference_id", "customer_full_name"];

const FIELDS = [
  { key: "jo_number", label: "JO Number" },
  { key: "job_date", label: "Date", format: formatDate },
  { key: "team_number", label: "Team Number", type: "team_personnel_group" },
  { key: "account_number", label: "Account Number" },
  { key: "reference_id", label: "Reference ID" },
  { key: "customer_full_name", label: "Customer Name" },
  { key: "customer_mobile_number", label: "Mobile Number" },
  { key: "customer_alt_mobile_number", label: "Alt Mobile Number" },
  { key: "complete_address", label: "Address" },
  { key: "municipality", label: "Municipality" },
  { key: "barangay", label: "Barangay" },
  { key: "plan", label: "Plan" },
  { key: "jo_status", label: "JO Status", type: "status" },
  { key: "jo_status_remarks", label: "JO Status Remarks" },
  { key: "team_leader_name", label: "Sales Agent" },
  { key: "assigned_tl_name", label: "Assigned TL", type: "assigned_tl" },
  { key: "call_status", label: "Call Status", type: "call_status" },
  { key: "call_status_remarks", label: "Call Remarks" },
  { key: "time_in", label: "Time In" },
  { key: "time_out", label: "Time Out" },
  { key: "nap_id", label: "NAP CODE", type: "nap_group" },
  { key: "serial_number", label: "Modem Serial Number" },
  { key: "record_id", label: "IPTV Serial Number" },
  { key: "nap_photo", label: "Completion Photo", type: "nap_photo" },
];

const STATUS_OPTIONS = ["Pending", "Completed", "Cancelled", "Rescheduled"];

// Fields rendered by the grouped NAP / Completion Details card instead of inline.
// They remain in FIELDS so saveFields() and the history tab continue to use them.
const NAP_GROUP_KEYS = new Set(["nap_id", "serial_number", "record_id", "nap_photo"]);

export default function JobOrderDetailModal({ jobOrderId, onClose, onRefresh, historyMode = false, initialHistoryJobDate = null }) {
  const { canPerformLifecycle, canEditJO, canEditField, hasPerm, canEditBlankJoNumber, user } = useAuth();
  const [jo, setJo] = useState(null);
  // Cross-company override: holders may edit the team number on another
  // company's job order, and the team/personnel pickers should offer that
  // company's teams rather than the current user's.
  const canEditCrossCompanyTeam = hasPerm("jo.team_cross_company");
  const teamCompanyId = (canEditCrossCompanyTeam && jo?.company_id) ? jo.company_id : user?.company_id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { check: checkComp, ConflictDialog } = useCompositionGuard();
  const { confirm } = useConfirm();
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // null | "complete" | "cancel" | "reopen" | "returnToPending"
  const [confirmRemark, setConfirmRemark] = useState("");
  const [confirmCancelReasons, setConfirmCancelReasons] = useState([]);
  const [reopenReason, setReopenReason] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const [confirmSerialNumber, setConfirmSerialNumber] = useState("");
  const [confirmRecordId, setConfirmRecordId] = useState("");
  const [confirmNapCode, setConfirmNapCode] = useState("");
  const [confirmNapId, setConfirmNapId] = useState("");
  const [confirmNapPort, setConfirmNapPort] = useState("");
  const [confirmVacantPorts, setConfirmVacantPorts] = useState("");
  const [confirmNapPhoto, setConfirmNapPhoto] = useState(null);
  const [confirmTeamNumber, setConfirmTeamNumber] = useState("");
  const [confirmPersonnel1Id, setConfirmPersonnel1Id] = useState(null);
  const [confirmPersonnel2Id, setConfirmPersonnel2Id] = useState(null);
  const [callStatusOthers, setCallStatusOthers] = useState(false);
  const [activeTLs, setActiveTLs] = useState([]);
  const [activeTab, setActiveTab] = useState(historyMode ? "history" : "details"); // "details" | "history"
  const [selectedHistoryJobDate, setSelectedHistoryJobDate] = useState(initialHistoryJobDate);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [activeTeams, setActiveTeams] = useState([]);
  const [expandedPhoto, setExpandedPhoto] = useState(null); // blob URL of the completion photo

  const closePhoto = () => {
    setExpandedPhoto((url) => { if (url) URL.revokeObjectURL(url); return null; });
  };

  const openNapPhoto = async () => {
    try {
      const res = await api.get(`/job-orders/${jobOrderId}/nap-photo`, { responseType: "blob" });
      setExpandedPhoto(URL.createObjectURL(res.data));
    } catch {
      setMessage("Completion photo is not available.");
    }
  };

  useEffect(() => {
    api.get("/team-leaders/active").then(({ data }) => setActiveTLs(data)).catch(() => {});
    api.get("/team-masters/active", { params: { company_id: teamCompanyId } })
      .then(({ data }) => setActiveTeams(data)).catch(() => {});
  }, [teamCompanyId]);

  useEffect(() => {
    if (!jobOrderId) return;
    setActiveTab(historyMode ? "history" : "details");
    setSelectedHistoryJobDate(historyMode ? initialHistoryJobDate : null);
    api.get(`/job-orders/${jobOrderId}`)
      .then(({ data }) => {
        setJo(data);
        setForm(data);
        setCallStatusOthers(data.call_status ? !isStandardCallStatus(data.call_status) : false);
        // Try to match team_number to a team master
        if (data.team_number) {
          const matchCompanyId = (canEditCrossCompanyTeam && data.company_id) ? data.company_id : user?.company_id;
          api.get("/team-masters/active", { params: { company_id: matchCompanyId } })
            .then(({ data: teams }) => {
              const match = teams.find((t) => t.team_number === data.team_number);
              if (match) setSelectedTeamId(match.id);
            }).catch(() => {});
        }
      })
      .catch(() => {
        onClose();
      });
  }, [jobOrderId, historyMode, initialHistoryJobDate]);

  if (!jo) return null;

  const isTerminalStatus = jo.jo_status === "Completed" || jo.jo_status === "Cancelled";
  const canOverrideTerminalTeam = hasPerm("jo.edit_terminal_team");
  const TERMINAL_TEAM_FIELDS = new Set(["team_number", "personnel_names", "personnel_1_id", "personnel_2_id", "assigned_tl_name", "assigned_tl_id"]);
  const joNumberIsBlank = !jo.jo_number || !jo.jo_number.trim() || jo.jo_number.trim() === "-";
  const isReadOnly = (key) => {
    if (key === "jo_number") {
      // Blank JO Number can be edited with proper permission, even on terminal status,
      // but never in history mode (historical snapshots are immutable here).
      return !(canEditBlankJoNumber && joNumberIsBlank && !historyMode);
    }
    if (READ_ONLY.includes(key) || !canEditField(key)) return true;
    if ((isTerminalStatus || historyMode) && canOverrideTerminalTeam && TERMINAL_TEAM_FIELDS.has(key)) return false;
    if (isTerminalStatus || historyMode) return true;
    return false;
  };
  const meta = jo.xlsx_metadata || {};
  const historyCount = jo.history?.length || 0;

  const selectedHistoryEntry = historyMode && selectedHistoryJobDate
    ? jo.history?.find((entry) => String(entry.job_date) === String(selectedHistoryJobDate))
    : null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveFields = async (overrides = {}) => {
    const body = {};
    FIELDS.forEach(({ key }) => {
      if (!READ_ONLY.includes(key) && key !== "parent_jo_number") {
        body[key] = form[key] ?? null;
      }
    });
    if (canEditBlankJoNumber && joNumberIsBlank && form.jo_number && form.jo_number.trim() && form.jo_number.trim() !== "-") {
      body.jo_number = form.jo_number.trim();
    }
    // Always include assigned_tl_id alongside assigned_tl_name
    if ("assigned_tl_name" in body) {
      body.assigned_tl_id = form.assigned_tl_id ?? null;
    }
    // Include personnel IDs
    body.personnel_1_id = form.personnel_1_id ?? null;
    body.personnel_2_id = form.personnel_2_id ?? null;
    // NAP completion fields beyond nap_id (nap_id is covered by the FIELDS loop)
    body.nap_code = form.nap_code || null;
    body.nap_port = form.nap_port || null;
    body.vacant_ports = vacantPortsPayload(form.vacant_ports);

    // For terminal/history records with override permission, use appropriate endpoint
    if (historyMode && canOverrideTerminalTeam) {
      const histEntry = jo.history?.find((h) => String(h.job_date) === String(selectedHistoryJobDate));
      if (histEntry) {
        await api.put(`/job-orders/history/${histEntry.id}/team`, {
          team_number: form.team_number ?? null,
          personnel_1_id: form.personnel_1_id ?? null,
          personnel_2_id: form.personnel_2_id ?? null,
          assigned_tl_id: form.assigned_tl_id ?? null,
          assigned_tl_name: form.assigned_tl_name ?? null,
        });
        return;
      }
    }
    await api.put(`/job-orders/${jo.id}`, { ...body, ...overrides });
  };

  const handleSave = async () => {
    // Validate: Call Status = Cancelled requires Call Remarks
    if (form.call_status === "Cancelled" && !(form.call_status_remarks || "").trim()) {
      setMessage("Call Remarks are required when setting Call Status to Cancelled.");
      return;
    }
    const ok = await checkComp({ teamNumber: form.team_number, assignmentDate: form.job_date, personnel1Id: form.personnel_1_id, personnel2Id: form.personnel_2_id });
    if (!ok) return;
    setSaving(true);
    setMessage(null);
    try {
      await saveFields();
      // If call_status was set to Cancelled, the backend auto-cancels the JO - refresh state
      if (form.call_status === "Cancelled" && hasPerm("field.edit.call_status")) {
        const { data } = await api.get(`/job-orders/${jo.id}`);
        setJo(data);
        setForm(data);
        setMessage("Job Order auto-cancelled (Call Status = Cancelled).");
      } else {
        setMessage("Job Order updated successfully.");
      }
      onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (finalRemark, reasons, teamNumber, p1Id, p2Id) => {
    setSaving(true);
    setConfirmAction(null);
    try {
      await api.post(`/job-orders/${jo.id}/cancel`, {
        jo_status_remarks: finalRemark,
        cancellation_reasons: reasons,
        team_number: teamNumber || null,
        personnel_1_id: p1Id,
        personnel_2_id: p2Id,
      });
      setMessage("Job Order cancelled.");
      onRefresh();
      onClose();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to cancel.");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (remark, serialNumber, recordId, teamNumber, p1Id, p2Id, napCode, napId, napPort, vacantPorts, napPhoto) => {
    // Keep the confirm dialog mounted (showing its spinner) while the request — which
    // re-fetches NAP details from the external API — runs.
    setSaving(true);
    try {
      await saveFields({ jo_status_remarks: remark, serial_number: serialNumber, record_id: recordId || null, nap_code: napCode, nap_id: napId, nap_port: napPort, vacant_ports: vacantPortsPayload(vacantPorts), team_number: teamNumber, personnel_1_id: p1Id, personnel_2_id: p2Id });
      if (napPhoto) {
        const photoForm = new FormData();
        photoForm.append("file", napPhoto);
        await api.post(`/job-orders/${jo.id}/nap-photo`, photoForm);
      }
      const { data } = await api.post(`/job-orders/${jo.id}/complete`);
      setSaving(false);
      setConfirmAction(null);
      await confirm({
        title: "Job Order Completed",
        message: data?.nap_details_updated
          ? "Job order completed successfully and NAP details updated."
          : "Job order completed successfully.",
        confirmLabel: "OK",
        cancelLabel: "OK",
      });
      onRefresh();
      onClose();
    } catch (err) {
      setSaving(false);
      setMessage(err.response?.data?.detail || "Failed to complete.");
    }
  };

  const handleRescheduleComplete = (updatedJo) => {
    setShowReschedule(false);
    setJo(updatedJo);
    setForm(updatedJo);
    setMessage("Job Order successfully rescheduled.");
    onRefresh();
  };

  const handleReopen = async () => {
    const trimmed = reopenReason.trim();
    if (!trimmed) return;
    setSaving(true);
    setConfirmAction(null);
    try {
      const { data } = await api.post(`/job-orders/${jo.id}/reopen`, { reopen_reason: trimmed });
      setJo(data);
      setForm(data);
      setReopenReason("");
      setMessage("Job Order reopened successfully.");
      onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to reopen.");
    } finally {
      setSaving(false);
    }
  };

  const handleReturnToPending = async () => {
    const trimmed = returnReason.trim();
    if (!trimmed) return;
    setSaving(true);
    setConfirmAction(null);
    try {
      const { data } = await api.post(`/job-orders/${jo.id}/return-to-pending`, { reason: trimmed });
      setJo(data);
      setForm(data);
      setReturnReason("");
      setMessage("Job Order returned to Pending successfully.");
      onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to return to pending.");
    } finally {
      setSaving(false);
    }
  };

  // Save handler for the grouped NAP / Completion Details card. Uploads a new
  // photo (if picked) then persists the NAP fields via the dedicated endpoint,
  // which also moves the NAP job-history and refreshes cached port counts.
  const handleNapCardSave = async ({ napCode, napId, napPort, vacantPorts, modemSerial, iptvSerial, reason, napPhotoFile }) => {
    if (napPhotoFile) {
      const fd = new FormData();
      fd.append("file", napPhotoFile);
      await api.post(`/job-orders/${jo.id}/nap-photo`, fd);
    }
    const { data } = await api.put(`/job-orders/${jo.id}/nap-details`, {
      nap_code: napCode,
      nap_id: napId,
      nap_port: napPort,
      vacant_ports: vacantPortsPayload(vacantPorts),
      serial_number: modemSerial,
      record_id: iptvSerial || null,
      reason,
    });
    return data;
  };

  const handleNapCardSaved = async () => {
    // Re-fetch full detail so history/metadata and has_nap_photo stay in sync.
    const { data } = await api.get(`/job-orders/${jo.id}`);
    setJo(data);
    setForm(data);
    setMessage("NAP / Completion details updated successfully.");
    onRefresh();
  };

  const renderField = (field) => {
    const { key, label, type, format, readOnly: alwaysReadOnly } = field;
    const ro = alwaysReadOnly || isReadOnly(key);
    let value = form[key] ?? "";
    if (key === "jo_number" && !ro && (value === "-" || value === "—")) value = "";

    // Completion photo — view-only link; rendered only when a photo is stored
    if (type === "nap_photo") {
      if (!jo?.has_nap_photo) return null;
      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <button
            type="button"
            onClick={openNapPhoto}
            title="View the NAP/completion photo"
            className="w-full inline-flex items-center justify-center gap-1.5 border rounded px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
          >
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4-4 4 4 4-6 4 6M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
            </svg>
            View Photo
          </button>
        </div>
      );
    }

    // Team & Personnel — combined group with two personnel dropdowns
    if (type === "team_personnel_group") {
      // Company-based team lock: locked if another company assigned the team
      // (bypassed for holders of the cross-company permission).
      const isTeamLocked = !canEditCrossCompanyTeam && jo.team_assigned_by_company && user?.company_id && jo.team_assigned_by_company !== user.company_id;
      if (ro || isTeamLocked) {
        const p1Name = form.personnel_1_name || "";
        const p2Name = form.personnel_2_name || "";
        const personnelDisplay = [p1Name, p2Name].filter(Boolean).join(", ") || form.personnel_names || "—";
        return (
          <div key={key} className="col-span-full">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Team Number</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{form.team_number || "—"} {isTeamLocked && !ro && <span title="Locked — assigned by another company">🔒</span>}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Personnel</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{personnelDisplay}</p>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div key={key} className="col-span-full">
          <TeamPersonnelGroup
            teamMasterId={selectedTeamId}
            personnel1Id={form.personnel_1_id}
            personnel2Id={form.personnel_2_id}
            onTeamChange={(teamId, teamData) => {
              setSelectedTeamId(teamId);
              if (teamData) {
                handleChange("team_number", teamData.team_number);
              } else {
                handleChange("team_number", "");
              }
            }}
            onPersonnel1Change={(id) => handleChange("personnel_1_id", id)}
            onPersonnel2Change={(id) => handleChange("personnel_2_id", id)}
            companyId={teamCompanyId}
            assignmentDate={form.job_date}
          />
        </div>
      );
    }

    // NAP completion fields — NAP Code, NAP ID, NAP Port, Vacant Ports
    if (type === "nap_group") {
      if (ro) {
        const isCompleted = (form.jo_status || jo?.jo_status) === "Completed";
        const cells = [
          ["NAP ID", form.nap_id],
          ["NAP Port", form.nap_port],
          ["Vacant Ports", form.vacant_ports],
        ];
        return (
          <div key={key} className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">NAP Code</label>
              {isCompleted && form.nap_code
                ? <NapCodeMapLinks napCode={form.nap_code} />
                : <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{form.nap_code || "—"}</p>}
            </div>
            {cells.map(([lbl, val]) => (
              <div key={lbl}>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">{lbl}</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">
                  {val === 0 ? "0" : (val || "—")}
                </p>
              </div>
            ))}
          </div>
        );
      }
      return (
        <div key={key} className="col-span-full">
          <NapFieldsGroup
            napCode={form.nap_code} onNapCodeChange={(v) => handleChange("nap_code", v)}
            napId={form.nap_id} onNapIdChange={(v) => handleChange("nap_id", v)}
            napPort={form.nap_port} onNapPortChange={(v) => handleChange("nap_port", v)}
            vacantPorts={form.vacant_ports} onVacantPortsChange={(v) => handleChange("vacant_ports", v)}
            labelClassName="block text-xs font-medium text-gray-500 mb-0.5"
          />
        </div>
      );
    }

    if (ro) {
      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">
            {format ? format(value) : (value || "—")}
          </p>
        </div>
      );
    }

    if (type === "status") {
      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">
            {value || "Pending"}
          </p>
        </div>
      );
    }

    if (type === "assigned_tl") {
      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <SearchableTLSelect
            options={activeTLs}
            value={form.assigned_tl_id || null}
            onChange={(tlId) => {
              const tl = activeTLs.find((t) => t.id === tlId);
              setForm((prev) => ({ ...prev, assigned_tl_id: tlId, assigned_tl_name: tl ? tl.name : null }));
            }}
          />
        </div>
      );
    }

    if (type === "call_status") {
      const currentValue = value || "";
      const isOthersMode = callStatusOthers;
      const dropdownValue = currentValue === "" && !isOthersMode ? "" : isStandardCallStatus(currentValue) ? currentValue : CALL_STATUS_OTHERS;
      const customText = isOthersMode ? currentValue : "";

      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <select
            value={dropdownValue}
            onChange={(e) => {
              const sel = e.target.value;
              if (sel === CALL_STATUS_OTHERS) {
                setCallStatusOthers(true);
                handleChange(key, "");
              } else {
                setCallStatusOthers(false);
                handleChange(key, sel);
              }
            }}
            className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Select —</option>
            {CALL_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
            <option value={CALL_STATUS_OTHERS}>{CALL_STATUS_OTHERS}</option>
          </select>
          {isOthersMode && (
            <input
              type="text"
              placeholder="Enter custom call status"
              value={customText}
              onChange={(e) => handleChange(key, e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>
      );
    }

    if (type === "number") {
      return (
        <div key={key}>
          <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    }

    return (
      <div key={key}>
        <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder={key === "jo_number" ? "Enter JO Number" : undefined}
          className={`w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500${key === "jo_number" ? " border-blue-300 bg-blue-50/30" : ""}`}
        />
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-10 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 my-8 flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Job Order Details — {jo.jo_number}</h2>
              {jo.jo_status === "Cancelled" && hasPerm("jo.reopen") && !historyMode && (
                <button
                  onClick={() => setConfirmAction("reopen")}
                  disabled={saving}
                  className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                >
                  Reopen
                </button>
              )}
              {jo.jo_status === "Completed" && hasPerm("jo.return_to_pending") && !historyMode && (
                <button
                  onClick={() => setConfirmAction("returnToPending")}
                  disabled={saving}
                  className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                >
                  Return to Pending
                </button>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
          </div>

          {/* Body: sidebar + content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-48 shrink-0 border-r bg-gray-50 flex flex-col py-4 gap-1 px-2">
              <button
                onClick={() => setActiveTab("details")}
                className={`text-left px-3 py-2 rounded text-sm font-medium ${
                  activeTab === "details"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                Job Order Details
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`text-left px-3 py-2 rounded text-sm font-medium flex items-center justify-between ${
                  activeTab === "history"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>Reschedule History</span>
                {historyCount > 0 && (
                  <span className={`text-xs rounded-full px-1.5 py-0.5 ${
                    activeTab === "history" ? "bg-white text-blue-700" : "bg-gray-300 text-gray-700"
                  }`}>
                    {historyCount}
                  </span>
                )}
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {activeTab === "details" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {FIELDS.filter((f) => !NAP_GROUP_KEYS.has(f.key)).map(renderField)}

                  {/* NAP / Completion Details — grouped, permission-gated inline edit */}
                  <div className="col-span-full">
                    <NapCompletionCard
                      napCode={form.nap_code}
                      napId={form.nap_id}
                      napPort={form.nap_port}
                      vacantPorts={form.vacant_ports}
                      modemSerial={form.serial_number}
                      iptvSerial={form.record_id}
                      hasPhoto={!!jo?.has_nap_photo}
                      isTerminal={isTerminalStatus}
                      completed={(form.jo_status || jo?.jo_status) === "Completed"}
                      canEdit={!historyMode && (isTerminalStatus ? hasPerm("jo.edit_nap_terminal") : canEditField("nap_id"))}
                      onViewPhoto={openNapPhoto}
                      onSave={handleNapCardSave}
                      onSaved={handleNapCardSaved}
                    />
                  </div>

                  {/* XLSX Metadata Collapsible Sections */}
                  {Object.keys(meta).length > 0 && (
                    <>
                      <CollapsibleSection title="Application Information">
                        <MetaField label="Reference ID" value={meta.reference_id} />
                        <MetaField label="Application Status" value={meta.application_status} />
                        <MetaField label="Status Updated On" value={meta.status_updated_on} />
                        <MetaField label="Application Submitted On" value={meta.application_submitted_on} />
                        <MetaField label="NAP ID" value={meta.nap_id} />
                        <MetaField label="Application Type" value={meta.application_type} />
                        <MetaField label="Source" value={meta.source} />
                        <MetaField label="Encoded By" value={meta.encoded_by} />
                        <MetaField label="Validated By" value={meta.validated_by} />
                        <MetaField label="Validator Email" value={meta.validator_email} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Customer / Personal Information">
                        <MetaField label="Customer Type" value={meta.customer_type} />
                        <MetaField label="Segment" value={meta.segment} />
                        <MetaField label="Title" value={meta.title} />
                        <MetaField label="Suffix" value={meta.suffix} />
                        <MetaField label="DOB" value={meta.dob} />
                        <MetaField label="Age" value={meta.age} />
                        <MetaField label="Nationality" value={meta.nationality} />
                        <MetaField label="Notification Language" value={meta.notification_language} />
                        <MetaField label="Notification Mode" value={meta.notification_mode} />
                        <MetaField label="Designation / Position" value={meta.designation_position} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Business Information">
                        <MetaField label="Business Type" value={meta.business_type} />
                        <MetaField label="Business Name" value={meta.business_name} />
                        <MetaField label="TIN" value={meta.tin} />
                        <MetaField label="Industry Type" value={meta.industry_type} />
                        <MetaField label="Department" value={meta.department} />
                        <MetaField label="Company ID No." value={meta.company_id_no} />
                        <MetaField label="Employer Name" value={meta.employer_name} />
                        <MetaField label="Employer Address" value={meta.employer_address} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Location / Address Information">
                        <MetaField label="Province" value={meta.province} />
                        <MetaField label="Region" value={meta.region} />
                        <MetaField label="Latitude" value={meta.latitude} />
                        <MetaField label="Longitude" value={meta.longitude} />
                        <MetaField label="Home Ownership" value={meta.home_ownership} />
                        <MetaField label="Address Type" value={meta.address_type} />
                        <MetaField label="Special Economic Zone" value={meta.special_economic_zone} />
                        <MetaField label="Zipcode" value={meta.zipcode} />
                        <MetaField label="Subdivision / Village" value={meta.subdivision_village} />
                        <MetaField label="Building / Condominium" value={meta.building_condominium} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Plan / Billing / Payment Information">
                        <MetaField label="Account Type" value={meta.account_type} />
                        <MetaField label="Billing Frequency" value={meta.billing_frequency} />
                        <MetaField label="Add Ons" value={meta.add_ons} />
                        <MetaField label="Promo Code" value={meta.promo_code} />
                        <MetaField label="Sales Promo Code" value={meta.sales_promo_code} />
                        <MetaField label="Payment Mode" value={meta.payment_mode} />
                        <MetaField label="Upfront Fee Payment Options" value={meta.upfront_fee_payment_options} />
                        <MetaField label="Suggested Installation Date" value={meta.suggested_installation_date} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Sales / Agency Information">
                        <MetaField label="Agent Type" value={meta.agent_type} />
                        <MetaField label="Sales Agent Code" value={meta.sales_agent_code} />
                        <MetaField label="Agency Name" value={meta.agency_name} />
                        <MetaField label="TSM/KAM" value={meta.tsm_kam} />
                        <MetaField label="Referral Type" value={meta.referral_type} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Contact Information">
                        <MetaField label="Landline No." value={meta.landline_no} />
                        <MetaField label="Email" value={meta.email} />
                        <MetaField label="Alternate Email" value={meta.alternate_email} />
                      </CollapsibleSection>

                      <CollapsibleSection title="Reason / Notes">
                        <MetaField label="Reason" value={meta.reason} />
                      </CollapsibleSection>
                    </>
                  )}
                </div>
              )}

              {activeTab === "history" && (
                <div>
                  {historyCount === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">No reschedule history.</p>
                  ) : (
                    <div className="space-y-4">
                      {jo.history
                        .slice()
                        .sort((a, b) => new Date(b.rescheduled_at) - new Date(a.rescheduled_at))
                        .map((entry, idx) => (
                          <div
                            key={entry.id}
                            className={`border rounded-lg p-4 ${
                              selectedHistoryEntry && entry.id === selectedHistoryEntry.id
                                ? "bg-blue-50 border-blue-300"
                                : "bg-gray-50"
                            }`}
                          >
                            <p className="text-xs font-semibold text-gray-500 mb-3">
                              Sched {historyCount - idx} &mdash; rescheduled on {new Date(entry.rescheduled_at).toLocaleString()}
                              {selectedHistoryEntry && entry.id === selectedHistoryEntry.id && (
                                <span className="ml-2 text-blue-600">(selected)</span>
                              )}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {FIELDS.map(({ key, label, format }) => (
                                <div key={key}>
                                  <label className="block text-xs font-medium text-gray-500 mb-0.5">{label}</label>
                                  <p className="text-sm text-gray-800 bg-white rounded px-3 py-1.5 border">
                                    {format
                                      ? format(entry[key])
                                      : (entry[key] != null && entry[key] !== "" ? String(entry[key]) : "—")}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {message && (
            <div className="px-6 py-2 shrink-0">
              <p className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">{message}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t shrink-0">
            {!isTerminalStatus && !historyMode && canPerformLifecycle && (
              <>
                {hasPerm("jo.complete") && (
                  <button
                    onClick={() => { setConfirmAction("complete"); setConfirmRemark(form.jo_status_remarks || ""); setConfirmSerialNumber(form.serial_number || ""); setConfirmRecordId(form.record_id || ""); setConfirmNapCode(form.nap_code || ""); setConfirmNapId(form.nap_id || ""); setConfirmNapPort(form.nap_port || ""); setConfirmVacantPorts(form.vacant_ports ?? ""); setConfirmTeamNumber(form.team_number || ""); setConfirmPersonnel1Id(form.personnel_1_id || null); setConfirmPersonnel2Id(form.personnel_2_id || null); }}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Complete Job Order
                  </button>
                )}
                {hasPerm("jo.reschedule") && (
                  <button
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await saveFields();
                        setShowReschedule(true);
                      } catch (err) {
                        setMessage(err.response?.data?.detail || "Failed to save before rescheduling.");
                      } finally {
                        setSaving(false);
                      }
                    }}
                    className="px-4 py-2 text-sm border border-yellow-400 text-yellow-700 rounded hover:bg-yellow-50"
                  >
                    Reschedule Job Order
                  </button>
                )}
                {hasPerm("jo.cancel") && (
                  <button
                    onClick={() => { setConfirmAction("cancel"); setConfirmRemark(form.jo_status_remarks || ""); setConfirmCancelReasons([]); setConfirmTeamNumber(form.team_number || ""); setConfirmPersonnel1Id(form.personnel_1_id || null); setConfirmPersonnel2Id(form.personnel_2_id || null); }}
                    className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50"
                  >
                    Cancel Job Order
                  </button>
                )}
              </>
            )}
            {((!isTerminalStatus && !historyMode && canEditJO) || ((isTerminalStatus || historyMode) && canOverrideTerminalTeam)) && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {showReschedule && (
        <RescheduleModal
          jobOrderId={jo.id}
          currentJobDate={jo.job_date}
          ageingOriginDate={jo.ageing_origin_date}
          initialRemark={form.jo_status_remarks || ""}
          initialTeamNumber={form.team_number || ""}
          initialPersonnel1Id={form.personnel_1_id || null}
          initialPersonnel2Id={form.personnel_2_id || null}
          teamAssignedByCompany={jo.team_assigned_by_company}
          onClose={() => setShowReschedule(false)}
          onComplete={handleRescheduleComplete}
        />
      )}

      {confirmAction === "complete" && (
        <CompleteJobOrderDialog
          teamNumber={confirmTeamNumber}
          onTeamNumberChange={setConfirmTeamNumber}
          personnel1Id={confirmPersonnel1Id}
          onPersonnel1IdChange={setConfirmPersonnel1Id}
          personnel2Id={confirmPersonnel2Id}
          onPersonnel2IdChange={setConfirmPersonnel2Id}
          companyId={teamCompanyId}
          assignmentDate={form.job_date}
          teamLocked={!canEditCrossCompanyTeam && !!(jo.team_assigned_by_company && user?.company_id && jo.team_assigned_by_company !== user.company_id)}
          remark={confirmRemark}
          onRemarkChange={setConfirmRemark}
          serialNumber={confirmSerialNumber}
          onSerialNumberChange={setConfirmSerialNumber}
          recordId={confirmRecordId}
          onRecordIdChange={setConfirmRecordId}
          napCode={confirmNapCode}
          onNapCodeChange={setConfirmNapCode}
          napId={confirmNapId}
          onNapIdChange={setConfirmNapId}
          napPort={confirmNapPort}
          onNapPortChange={setConfirmNapPort}
          vacantPorts={confirmVacantPorts}
          onVacantPortsChange={setConfirmVacantPorts}
          napPhoto={confirmNapPhoto}
          onNapPhotoChange={setConfirmNapPhoto}
          onConfirm={() => handleComplete(confirmRemark, confirmSerialNumber, confirmRecordId, confirmTeamNumber, confirmPersonnel1Id, confirmPersonnel2Id, confirmNapCode, confirmNapId, confirmNapPort, confirmVacantPorts, confirmNapPhoto)}
          onCancel={() => setConfirmAction(null)}
          submitting={saving}
        />
      )}

      {confirmAction === "cancel" && (
        <CancelJobOrderDialog
          teamNumber={confirmTeamNumber}
          onTeamNumberChange={setConfirmTeamNumber}
          personnel1Id={confirmPersonnel1Id}
          onPersonnel1IdChange={setConfirmPersonnel1Id}
          personnel2Id={confirmPersonnel2Id}
          onPersonnel2IdChange={setConfirmPersonnel2Id}
          remark={confirmRemark}
          onRemarkChange={setConfirmRemark}
          reasons={confirmCancelReasons}
          onReasonsChange={setConfirmCancelReasons}
          companyId={teamCompanyId}
          assignmentDate={form.job_date}
          teamLocked={!canEditCrossCompanyTeam && !!(jo.team_assigned_by_company && user?.company_id && jo.team_assigned_by_company !== user.company_id)}
          onConfirm={handleCancel}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {confirmAction === "reopen" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Reopen Job Order</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to reopen this cancelled job order? The status will be changed back to <span className="font-semibold text-blue-700">Pending</span>.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reopen reason <span className="text-red-500">*</span></label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                rows={3}
                placeholder="Enter reason for reopening this job order..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
              {reopenReason.length > 0 && !reopenReason.trim() && (
                <p className="text-xs text-red-500 mt-1">Reopen reason cannot be blank spaces only.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setConfirmAction(null); setReopenReason(""); }}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReopen}
                disabled={!reopenReason.trim()}
                className="px-4 py-2 text-sm text-white rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Reopen
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction === "returnToPending" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Return Job Order to Pending</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to return this completed job order to <span className="font-semibold text-blue-700">Pending</span>? Use this to undo an accidental completion.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                rows={3}
                placeholder="Enter reason for returning this job order to pending..."
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
              />
              {returnReason.length > 0 && !returnReason.trim() && (
                <p className="text-xs text-red-500 mt-1">Reason cannot be blank spaces only.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setConfirmAction(null); setReturnReason(""); }}
                className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReturnToPending}
                disabled={!returnReason.trim()}
                className="px-4 py-2 text-sm text-white rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Return to Pending
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Completion photo lightbox */}
      {expandedPhoto && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80" onClick={closePhoto}>
          <img src={expandedPhoto} alt="Completion Photo" className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl" />
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white text-xl hover:bg-white/40" onClick={closePhoto}>×</button>
        </div>
      )}

      <ConflictDialog />
    </>
  );
}

